<template>
  <div class="on-task-update-labels-form">
    <div class="labels-form-header">
      <label>{{ labelText }}</label>
      <button
        type="button"
        v-if="canStartEditing"
        class="btn-icon labels-edit-btn"
        :title="editButtonTitle"
        @click.stop.prevent="startEditing"
      >
        <EditIcon />
      </button>
    </div>
    <div v-if="!editing" style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap">
      <LabelBadge
        v-for="label in taskLabels"
        :key="label.id"
        :label="labelForModal(label)"
        :clickable="scope === 'personal' || (scope === 'project' && !!projectId)"
        :personal="scope === 'personal'"
        @click-label="openLabelTasksModal"
      />
      <span v-if="!taskLabels || taskLabels.length === 0" class="muted">{{ emptyMessage }}</span>
    </div>
    <div v-else>
      <MultiSelect
        v-if="availableLabels.length > 0"
        :options="availableLabels"
        :selected-ids="selectedLabelIds"
        :label="''"
        :placeholder="s__('TaskForm|Select labels...')"
        :search-placeholder="s__('TaskForm|Search labels...')"
        :no-results-text="s__('TaskForm|No labels found')"
        @update:selected-ids="selectedLabelIds = $event"
      />
      <div v-else class="muted" style="margin-bottom: 8px">{{ emptyMessage }}</div>
      <div
        style="
          display: flex;
          gap: 8px;
          margin-top: 8px;
          justify-content: space-between;
          align-items: center;
        "
      >
        <div style="display: flex; gap: 8px">
          <button type="button" class="btn-sm btn-primary" @click.stop.prevent="saveLabels">
            {{ s__('TaskDetail|Save') }}
          </button>
          <button type="button" class="btn-sm btn-secondary" @click.stop.prevent="cancelEditing">
            {{ s__('TaskDetail|Cancel') }}
          </button>
        </div>
        <button
          type="button"
          v-if="canCreateLabel"
          class="btn-sm btn-outline"
          @click.stop.prevent="showCreateModal = true"
        >
          {{ createButtonTitle }}
        </button>
      </div>
    </div>

    <!-- Create Label Modal -->
    <LabelModal
      v-model="showCreateModal"
      :title="createModalTitle"
      :creating="creating"
      :error="error"
      @submit="handleCreateLabel"
      @close="handleModalClose"
    />
    <LabelTasksModal
      :visible="!!selectedLabel"
      :label="selectedLabel"
      :scope="selectedLabelScope"
      :project-id="selectedLabel?.projectId || selectedLabel?.project_id || projectId"
      :chat-id="chatId"
      :from-id="fromId"
      @label-click="openScopedLabelTasksModal"
      @close="selectedLabel = null"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useMutation, useLazyQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__ } from '@/locale'
import LabelModal from './LabelModal.vue'
import LabelBadge from './LabelBadge.vue'
import LabelTasksModal from './LabelTasksModal.vue'
import MultiSelect from './MultiSelect.vue'
import { useCreateLabel } from '@/composables/useCreateLabel'
import EditIcon from './icons/EditIcon.vue'
import PlusIcon from './icons/PlusIcon.vue'

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: value => ['project', 'personal'].includes(value),
  },
  taskId: {
    type: String,
    required: true,
  },
  projectId: {
    type: String,
    required: false,
    default: null,
  },
  chatId: {
    type: String,
    default: null,
  },
  fromId: {
    type: String,
    default: null,
  },
  taskLabels: {
    type: Array,
    required: true,
    default: () => [],
  },
  availableLabels: {
    type: Array,
    required: true,
    default: () => [],
  },
  canCreateLabel: {
    type: Boolean,
    default: false,
  },
  labelText: {
    type: String,
    required: true,
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  editButtonTitle: {
    type: String,
    default: '',
  },
  createButtonTitle: {
    type: String,
    default: '',
  },
  createModalTitle: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:taskLabels', 'refetch'])

// State
const editing = ref(false)
const selectedLabelIds = ref([])
const showCreateModal = ref(false)
const selectedLabel = ref(null)
const selectedLabelScope = ref(props.scope)

const {
  creating,
  error,
  createLabel: createLabelMutation,
} = useCreateLabel(props.scope, props.projectId)

const canStartEditing = computed(
  () => !editing.value && (props.availableLabels.length > 0 || props.canCreateLabel)
)

// GraphQL mutations
const ATTACH_PROJECT_LABEL = gql`
  mutation AttachLabel($taskId: ID!, $labelId: ID!) {
    attachLabel(taskId: $taskId, labelId: $labelId) {
      task {
        id
        labels {
          id
          title
          color
        }
      }
      errors
    }
  }
`

const DETACH_PROJECT_LABEL = gql`
  mutation DetachLabel($taskId: ID!, $labelId: ID!) {
    detachLabel(taskId: $taskId, labelId: $labelId) {
      task {
        id
        labels {
          id
          title
          color
        }
      }
      errors
    }
  }
`

const ATTACH_PERSONAL_LABEL = gql`
  mutation AttachPersonalLabel($taskId: ID!, $personalLabelId: ID!) {
    attachPersonalLabel(taskId: $taskId, personalLabelId: $personalLabelId) {
      task {
        id
        personalLabels {
          id
          title
          color
        }
      }
      errors
    }
  }
`

const DETACH_PERSONAL_LABEL = gql`
  mutation DetachPersonalLabel($taskId: ID!, $personalLabelId: ID!) {
    detachPersonalLabel(taskId: $taskId, personalLabelId: $personalLabelId) {
      task {
        id
        personalLabels {
          id
          title
          color
        }
      }
      errors
    }
  }
`

// Setup mutations based on scope
const attachMutation = computed(() => {
  return props.scope === 'project' ? ATTACH_PROJECT_LABEL : ATTACH_PERSONAL_LABEL
})

const detachMutation = computed(() => {
  return props.scope === 'project' ? DETACH_PROJECT_LABEL : DETACH_PERSONAL_LABEL
})

const { mutate: attachLabelMutation } = useMutation(attachMutation)
const { mutate: detachLabelMutation } = useMutation(detachMutation)

// Watch taskLabels to update selectedLabelIds when not editing
watch(
  () => props.taskLabels,
  labels => {
    // Only update when not editing to avoid interfering with user selections
    if (!editing.value) {
      selectedLabelIds.value = (labels || []).map(l => String(l.id))
    }
  },
  { immediate: true, deep: true }
)

// Methods
async function startEditing(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  // Set editing to true first so MultiSelect component is rendered
  editing.value = true
  // Wait for next tick to ensure MultiSelect is mounted
  await nextTick()
  // Ensure selectedLabelIds is set from taskLabels when starting to edit
  // Convert to strings to match MultiSelect's expected format
  // GraphQL ID type is a string, but ensure we convert to string for consistency
  const labelIds = (props.taskLabels || []).map(l => {
    const id = l.id
    // Handle both string and number IDs from GraphQL - always convert to string
    return String(id)
  })
  // Force a new array reference to ensure Vue detects the change
  selectedLabelIds.value = labelIds.slice()
  // Wait for another tick to ensure MultiSelect receives the updated value
  await nextTick()
}

function cancelEditing(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  editing.value = false
  selectedLabelIds.value = (props.taskLabels || []).map(l => String(l.id))
}

async function saveLabels(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  try {
    const currentLabelIds = (props.taskLabels || []).map(l => String(l.id))
    const toAttach = selectedLabelIds.value.filter(id => !currentLabelIds.includes(String(id)))
    const toDetach = currentLabelIds.filter(id => !selectedLabelIds.value.includes(String(id)))

    let updatedLabels = [...(props.taskLabels || [])]

    // Attach new labels
    for (const labelId of toAttach) {
      const mutationVars =
        props.scope === 'project'
          ? { taskId: props.taskId, labelId: String(labelId) }
          : { taskId: props.taskId, personalLabelId: String(labelId) }

      const { data } = await attachLabelMutation(mutationVars)

      const resultField = props.scope === 'project' ? 'attachLabel' : 'attachPersonalLabel'
      const labelsField = props.scope === 'project' ? 'labels' : 'personalLabels'

      if (data?.[resultField]?.errors?.length) {
        throw new Error(data[resultField].errors.join(', '))
      }
      if (data?.[resultField]?.task?.[labelsField]) {
        updatedLabels = data[resultField].task[labelsField]
      }
    }

    // Detach removed labels
    for (const labelId of toDetach) {
      const mutationVars =
        props.scope === 'project'
          ? { taskId: props.taskId, labelId: String(labelId) }
          : { taskId: props.taskId, personalLabelId: String(labelId) }

      const { data } = await detachLabelMutation(mutationVars)

      const resultField = props.scope === 'project' ? 'detachLabel' : 'detachPersonalLabel'
      const labelsField = props.scope === 'project' ? 'labels' : 'personalLabels'

      if (data?.[resultField]?.errors?.length) {
        throw new Error(data[resultField].errors.join(', '))
      }
      if (data?.[resultField]?.task?.[labelsField]) {
        updatedLabels = data[resultField].task[labelsField]
      }
    }

    // Emit updated labels
    emit('update:taskLabels', updatedLabels)
    emit('refetch')

    editing.value = false
  } catch (e) {
    alert('Failed to update labels: ' + (e.message || 'Unknown error'))
  }
}

function handleModalClose() {
  showCreateModal.value = false
}

function labelForModal(label) {
  return {
    ...label,
    projectId: props.projectId,
  }
}

function openLabelTasksModal(label) {
  if (props.scope === 'project' && !props.projectId) return
  selectedLabelScope.value = props.scope
  selectedLabel.value = label
}

function openScopedLabelTasksModal(payload) {
  if (!payload?.label) return
  selectedLabelScope.value = payload.scope || props.scope
  selectedLabel.value = payload.label
}

async function handleCreateLabel(formData) {
  const result = await createLabelMutation(formData.title, formData.color)

  if (!result.success) {
    return // Error is already set in the composable
  }

  // Emit refetch to update available labels list
  emit('refetch')

  // If we're editing, add the new label to selectedLabelIds
  if (editing.value && result.label) {
    const currentIds = Array.isArray(selectedLabelIds.value) ? [...selectedLabelIds.value] : []
    if (!currentIds.includes(result.label.id)) {
      selectedLabelIds.value = [...currentIds, result.label.id]
    }
  }

  // Close modal
  showCreateModal.value = false
}
</script>

<style scoped>
.on-task-update-labels-form {
  padding-bottom: 12px;
}

.labels-form-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
}

.labels-form-header label {
  margin-bottom: 0;
}

.label-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
}

.label-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-top: 8px;
}

.label-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.label-checkbox input[type='checkbox'] {
  cursor: pointer;
}

.btn-icon {
  padding: 4px 8px;
  font-size: 14px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #f8f9fa;
  color: #495057;
}

.labels-edit-btn {
  margin-left: 4px;
}

.muted {
  color: #757575;
}

@media (max-width: 767px) {
  .labels-edit-btn {
    margin-left: auto;
  }
}
</style>
